import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Award,
  Settings,
  MapPin,
  Snowflake,
  Clock,
  HeartHandshake,
  ArrowRight,
  Send,
  Phone,
  Star,
  ShieldCheck,
  Check,
  Mail,
  MessageCircle
} from 'lucide-react';
import Hero from '../components/Hero';
import { services, whyChooseUs, contactInfo, testimonials } from '../mockData';

const Home = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    serviceType: 'Okul Servisi',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const whatsappMessage = `Merhaba,\n\nAd Soyad: ${formData.name}\nTelefon: ${formData.phone}\nHizmet: ${formData.serviceType}\nMesaj: ${formData.message}\n\nBilgi almak istiyorum.`;
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappNumber = contactInfo.whatsappFormatted;
    
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  // Why Us data with descriptions
  const whyUsData = [
    {
      icon: Award,
      title: "Belgeli ve Eğitimli Şoförler",
      desc: "SRC belgeli, düzenli eğitimli sürücüler."
    },
    {
      icon: Settings,
      title: "Düzenli Bakım – Günlük Kontrol",
      desc: "Araçlarımız periyodik bakım ve günlük kontrollerden geçer."
    },
    {
      icon: MapPin,
      title: "Planlı & Güvenli Güzergâh",
      desc: "Güzergâhlar güvenlik öncelikli planlanır."
    },
    {
      icon: Snowflake,
      title: "Kış Şartlarına Hazır",
      desc: "Erzurum kışına uygun donanım ve ekipman."
    }
  ];

  return (
    <div className="page-home">
      <Hero />

      {/* Services - 2x2 Grid */}
      <section className="section services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Hizmetlerimiz</h2>
            <p className="section-subtitle">
              Güvenli ve planlı taşımacılık çözümleri
            </p>
          </div>

          <div className="services-grid">
            {services.slice(0, 4).map((service) => (
              <div 
                key={service.id} 
                className="service-card"
                onClick={() => navigate(`/hizmet/${service.id}`)}
                data-testid={`service-card-${service.id}`}
              >
                <div className="service-card-bg">
                  <img src={service.image} alt={service.title} />
                </div>
                <div className="service-card-overlay"></div>
                <div className="service-card-content">
                  <h3>{service.title}</h3>
                  <ArrowRight size={16} className="service-card-arrow" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us - 2 Column Cards + Badges */}
      <section className="section why-section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Neden Vizyon Turizm?</h2>
          </div>

          {/* Trust Badges */}
          <div className="trust-badges">
            <div className="trust-badge">
              <Check size={14} />
              <span>Zamanında Servis</span>
            </div>
            <div className="trust-badge">
              <Check size={14} />
              <span>Güvenlik Öncelikli</span>
            </div>
            <div className="trust-badge">
              <Check size={14} />
              <span>7/24 İletişim</span>
            </div>
          </div>

          {/* Why Cards */}
          <div className="why-grid">
            {whyUsData.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="why-card">
                  <div className="why-icon">
                    <IconComponent size={22} />
                  </div>
                  <div className="why-content">
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials - Carousel Style */}
      <section className="section testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Müşteri Yorumları</h2>
          </div>

          <div className="testimonials-grid">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating">
                  <div className="stars">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} fill="#facc15" />
                    ))}
                  </div>
                  <span className="score">5.0</span>
                </div>
                <p className="testimonial-text">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="testimonial-info">
                    <span className="testimonial-name">{testimonial.name}</span>
                    <span className="testimonial-role">{testimonial.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Contact - Action Focused */}
      <section className="section contact-section section-alt">
        <div className="container">
          <div className="contact-grid">
            {/* Left - Action Buttons */}
            <div className="contact-info">
              <h2>Hızlı İletişim</h2>
              <p>Size özel fiyat teklifi için hemen ulaşın.</p>
              
              <div className="contact-actions">
                <a href={`tel:${contactInfo.phone}`} className="contact-action-btn">
                  <div className="contact-action-icon call">
                    <Phone size={22} />
                  </div>
                  <div className="contact-action-text">
                    <h4>Hemen Ara</h4>
                    <p>{contactInfo.phone}</p>
                  </div>
                </a>

                <a 
                  href="https://wa.me/905411492525" 
                  className="contact-action-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="contact-action-icon whatsapp">
                    <MessageCircle size={22} />
                  </div>
                  <div className="contact-action-text">
                    <h4>WhatsApp'tan Yaz</h4>
                    <p>{contactInfo.whatsapp}</p>
                  </div>
                </a>

                <a href={`mailto:${contactInfo.email}`} className="contact-action-btn">
                  <div className="contact-action-icon email">
                    <Mail size={22} />
                  </div>
                  <div className="contact-action-text">
                    <h4>E-posta Gönder</h4>
                    <p>{contactInfo.email}</p>
                  </div>
                </a>
              </div>

              <div className="contact-hours">
                <h4>Çalışma Saatleri</h4>
                <p>Pazartesi - Cumartesi: 07:00 - 20:00</p>
                <p>Pazar: 09:00 - 18:00</p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="contact-form-wrapper">
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <input
                    type="text"
                    name="name"
                    placeholder="Ad Soyad"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Telefon"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-phone-input"
                  />
                </div>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  data-testid="contact-service-select"
                >
                  <option value="Okul Servisi">Okul Servisi</option>
                  <option value="Öğrenci Taşımacılığı">Öğrenci Taşımacılığı</option>
                  <option value="Personel Taşımacılığı">Personel Taşımacılığı</option>
                </select>
                <textarea
                  name="message"
                  placeholder="Mesajınız"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  data-testid="contact-message-input"
                ></textarea>
                <button type="submit" className="btn btn-primary btn-full" data-testid="contact-submit-btn">
                  <Send size={18} />
                  WhatsApp ile Gönder
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky WhatsApp Button */}
      <a 
        href="https://wa.me/905411492525" 
        className="sticky-whatsapp"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        data-testid="sticky-whatsapp"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default Home;
